import type {
  GetApiServiceRequestsParams,
  ServiceRequestDto,
  ServiceRequestStatus,
  ServiceRequestType,
} from '@smartsash/api-client';
import { DEFAULT_PAGE, sortSpecSchema } from '@smartsash/data-screen';
import type { FilterCondition } from '@smartsash/ui';
import { z } from 'zod';

import { requestSubject } from './requestSubject';

import { ADMIN_LIST_SEARCH_DEFAULTS, adminListShape, LIST_PAGE_SIZE } from '@/router/listSearch';
import {
  personName,
  SERVICE_REQUEST_STATUSES,
  SERVICE_REQUEST_TYPES,
} from '@/ui/pages/ServiceRequestDetails/serviceRequestLabels';
import { toEndOfDayInstant, toStartOfDayInstant } from '@/utils/localDay';

/**
 * The admin Requests list: URL search, filter fields and the one mapping onto the endpoint's
 * query (HS-1548, E1, E2).
 *
 * `GET /api/service-requests` accepts only `institutionId`, `status`, `type`, `requesterUserId`,
 * `createdFrom`, `createdTo`, `archived`, `page` and `pageSize`; any other key is a 400
 * (`InvalidListQuery`).
 * So the shared list search's `q` and `sort` never leave the URL, the page is capped where the
 * API refuses it, and the page size where the API clamps it.
 */

/** AdminAPI answers `page > 1000000` with a 400 (`parameterName: "page"`). */
export const SERVICE_REQUESTS_MAX_PAGE = 1_000_000;

/** AdminAPI clamps `pageSize` to 100; asking for more would desynchronise the pager. */
export const SERVICE_REQUESTS_MAX_PAGE_SIZE = 100;

/** The builder field ids this list offers. */
export const SERVICE_REQUESTS_STATUS_FIELD = 'status';
export const SERVICE_REQUESTS_TYPE_FIELD = 'type';
export const SERVICE_REQUESTS_INSTITUTION_FIELD = 'institution';
export const SERVICE_REQUESTS_CREATED_FIELD = 'createdAt';

/**
 * The two views of the list (HS-1548 R2-D2, R2-A5). `active` is the default and never written to
 * the URL; `?view=archived` shows only archived requests (the endpoint's `archived=true`).
 */
export const SERVICE_REQUEST_VIEWS = ['active', 'archived'] as const;
export type ServiceRequestsView = (typeof SERVICE_REQUEST_VIEWS)[number];

export const serviceRequestsSearchSchema = z.object({
  page: z.coerce
    .number()
    .int()
    .positive()
    .catch(DEFAULT_PAGE)
    .transform((page) => Math.min(page, SERVICE_REQUESTS_MAX_PAGE)),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .catch(LIST_PAGE_SIZE)
    .transform((size) => Math.min(size, SERVICE_REQUESTS_MAX_PAGE_SIZE)),
  // Kept in the schema only so the factory's type is satisfied and a stale link does not fail
  // the route; neither reaches the endpoint (see `serviceRequestsQueryParams`).
  sort: sortSpecSchema.optional().catch(undefined),
  q: z.string().trim().optional().catch(undefined),
  filters: adminListShape.filters,
  /** A removable requester chip (A5), set from a row or the details page. */
  requesterUserId: z.guid().optional().catch(undefined),
  /** Absent means `active`; an unknown value falls back to it rather than failing the route. */
  view: z.enum(SERVICE_REQUEST_VIEWS).optional().catch(undefined),
});

export type ServiceRequestsSearch = z.infer<typeof serviceRequestsSearchSchema>;

function isStatus(value: string): value is ServiceRequestStatus {
  return (SERVICE_REQUEST_STATUSES as string[]).includes(value);
}

function isType(value: string): value is ServiceRequestType {
  return (SERVICE_REQUEST_TYPES as string[]).includes(value);
}

/** The single value of an `eq` condition on `field`, or `undefined`. */
function equalsValue(conditions: FilterCondition[], field: string): string | undefined {
  const condition = conditions.find((item) => item.field === field && item.operator === 'eq');
  const value = condition?.values[0];
  return value === undefined || value === '' ? undefined : value;
}

/**
 * The URL search → `GetApiServiceRequestsParams`. The one place the list's query is built: the
 * screen and the filter's cache read both call it.
 *
 * Dates follow A3: the created range opens at the start of its first local day and closes at the
 * end of its last one, both sent as UTC instants.
 */
export function serviceRequestsQueryParams(
  search: ServiceRequestsSearch,
): GetApiServiceRequestsParams {
  const conditions = search.filters ?? [];
  const params: GetApiServiceRequestsParams = {
    page: Math.min(search.page, SERVICE_REQUESTS_MAX_PAGE),
    pageSize: Math.min(search.pageSize, SERVICE_REQUESTS_MAX_PAGE_SIZE),
    // Sent in both views, so the two never share a cache entry by accident.
    archived: search.view === 'archived',
  };

  const status = equalsValue(conditions, SERVICE_REQUESTS_STATUS_FIELD);
  if (status !== undefined && isStatus(status)) params.status = status;

  // The API takes a repeatable `type` (HS-1541); this filter still picks one.
  const type = equalsValue(conditions, SERVICE_REQUESTS_TYPE_FIELD);
  if (type !== undefined && isType(type)) params.type = [type];

  const institutionId = equalsValue(conditions, SERVICE_REQUESTS_INSTITUTION_FIELD);
  if (institutionId !== undefined) params.institutionId = institutionId;

  const created = conditions.find(
    (item) => item.field === SERVICE_REQUESTS_CREATED_FIELD && item.operator === 'between',
  );
  if (created !== undefined) {
    const [from = '', to = ''] = created.values;
    const createdFrom = toStartOfDayInstant(from);
    const createdTo = toEndOfDayInstant(to);
    if (createdFrom !== undefined) params.createdFrom = createdFrom;
    if (createdTo !== undefined) params.createdTo = createdTo;
  }

  if (search.requesterUserId !== undefined) params.requesterUserId = search.requesterUserId;

  return params;
}

/** The search that opens the list narrowed to one requester (A5). */
export function serviceRequestsByRequester(requesterUserId: string, view?: ServiceRequestsView) {
  // `active` is the default and never written to the URL; an archived view is kept (R2-A5).
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    requesterUserId,
    ...(view === 'archived' ? { view } : {}),
  };
}

/** The search that opens the Archived view — where a request deleted from its page leads. */
export function serviceRequestsArchived() {
  return { ...ADMIN_LIST_SEARCH_DEFAULTS, view: 'archived' as const };
}

/** The search that opens the list narrowed to one status (the summary toast opens `New`). */
export function serviceRequestsWithStatus(status: ServiceRequestStatus) {
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    filters: [{ field: SERVICE_REQUESTS_STATUS_FIELD, operator: 'eq' as const, values: [status] }],
  };
}

/** The text each column holds for a row — the table's `value` readers. */
export const serviceRequestColumnText = {
  type: (row: ServiceRequestDto) => row.type,
  // HS-1542 step 14: the shared subject rule; no subject reads as empty, like the other columns.
  subject: (row: ServiceRequestDto) => requestSubject(row) ?? '',
  requester: (row: ServiceRequestDto) =>
    `${personName(row.requester) ?? ''} ${row.requester.email ?? ''}`.trim(),
  institution: (row: ServiceRequestDto) => row.institutionName ?? '',
  status: (row: ServiceRequestDto) => row.status,
  // HS-1542: read-only here. Like `requester`, untranslated text reads as empty: `null`
  // ("Unassigned") and an assignee with no usable name ("Unknown user") both give ''.
  assignee: (row: ServiceRequestDto) => personName(row.assignee) ?? '',
  createdAt: (row: ServiceRequestDto) => row.createdAt,
} as const;

export type ServiceRequestColumnId = keyof typeof serviceRequestColumnText;
